/**
 * 背景色に対して読める文字色を決めるユーティリティ（#3529）。
 *
 * キャンペーンごとに管理画面から設定される mainColor / subColor は組み合わせが自由なため、
 * 「淡いメインカラー × 白いサブカラー」のように文字がまったく読めない配色が成立してしまう。
 * ここでは WCAG 2.1 の相対輝度・コントラスト比を用いて、
 *
 *   1. キャンペーン指定色（sub-color）が AA を満たすならそれを尊重する
 *   2. 満たさないときだけ白 / 黒に退避する
 *
 * という方針で文字色を決定する（`resolveOnColor`）。
 */

/** WCAG 2.1 相対輝度（0..1）。https://www.w3.org/TR/WCAG21/#dfn-relative-luminance */
function relativeLuminance(hex: string): number {
  const matched = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim())
  if (!matched) {
    throw new Error(`relativeLuminance: invalid hex color "${hex}". Expected a 6-digit hex string like "#RRGGBB".`)
  }
  const clean = matched[1]
  const channel = (value: number): number => {
    const c = value / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const r = channel(Number.parseInt(clean.slice(0, 2), 16))
  const g = channel(Number.parseInt(clean.slice(2, 4), 16))
  const b = channel(Number.parseInt(clean.slice(4, 6), 16))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** 2 色のコントラスト比（1..21）。引数の順序は結果に影響しない */
export function contrastRatio(hexA: string, hexB: string): number {
  const l1 = relativeLuminance(hexA)
  const l2 = relativeLuminance(hexB)
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1]
  return (hi + 0.05) / (lo + 0.05)
}

export const CONTRAST_LIGHT = '#FFFFFF'

/**
 * 既存 Tailwind の `text-black`（tailwind.config.js の textColor.black）と同値。
 * 濃色側の**第一候補**。純黒 #000000 ではなくこの値を優先することは #3529 で決定済み。
 * 変更する場合は pages/_document.tsx 側のフォールバック値も合わせること。
 */
export const CONTRAST_DARK = '#262626'

/**
 * 濃色側の**退避先**。
 *
 * #262626 は純黒より相対輝度が高い（Y ≈ 0.0194）ぶんコントラストが弱く、
 * 白と #262626 の 2 択では **理論下限が 3.89:1** にしかならない
 * （両者が等しくなる Y ≈ 0.220、つまり #7B7B7B 前後の中間グレー背景）。
 * これは AA（4.5:1）を下回る。
 *
 * そこで「白 / #262626 のどちらも AA に届かない背景」に限り純黒へ退避する。
 * 白と純黒の 2 択なら理論下限は 4.58:1（Y ≈ 0.179）となり、AA を必ず満たせる。
 */
export const CONTRAST_DARK_STRONG = '#000000'

/** WCAG AA の最低コントラスト比（通常サイズのテキスト） */
export const WCAG_AA_RATIO = 4.5

/**
 * 背景色に対して読みやすい文字色（白 or 黒）を返す。**戻り値は必ず AA（4.5:1）以上**になる。
 *
 * **通常状態の背景 1 色だけで判定する。**
 * hover / pressed（primary-6 / primary-7）を含めた「最悪ケース最大化」方式は採用していない。
 * その方式だと、利用者がほぼ常に見ている通常状態のコントラストを AA 未満に落とす組み合わせが
 * 存在するため（背景輝度 Y≈0.20 で base: 白 4.20 / 黒 5.00、pressed: 白 6.25 / 黒 3.36 となり、
 * 最小値比較では白が選ばれて base が 4.20 まで落ちる）。
 *
 * 濃色は #262626（既存の text-black）を優先するが、白 / #262626 の 2 択では
 * 中間グレー背景で AA に届かない（理論下限 3.89:1）。そのケースに限り純黒へ退避する。
 */
export function getContrastTextColor(background: string): string {
  const ratioLight = contrastRatio(background, CONTRAST_LIGHT)
  const ratioDark = contrastRatio(background, CONTRAST_DARK)

  // 通常はここで決まる（#FFC500 のような明るい背景 → #262626 / 濃紺 → 白）
  if (Math.max(ratioLight, ratioDark) >= WCAG_AA_RATIO) {
    return ratioLight >= ratioDark ? CONTRAST_LIGHT : CONTRAST_DARK
  }

  // 白も #262626 も AA に届かない中間グレー背景のみ、純黒に退避して AA を確保する
  return ratioLight >= contrastRatio(background, CONTRAST_DARK_STRONG) ? CONTRAST_LIGHT : CONTRAST_DARK_STRONG
}

/**
 * 背景 `background` の上に置く文字色を決める。
 *
 * キャンペーンが指定した色（`preferred`）が AA を満たすならそれを尊重し、
 * **満たさないときだけ**白 / 黒に退避する。
 * 「読めているブランド配色は壊さず、読めないものだけ直す」ための関数。
 *
 * @param background 背景色（`#RRGGBB`）
 * @param preferred  キャンペーン指定の文字色。未設定・不正値なら白黒判定にフォールバックする
 */
export function resolveOnColor(background: string, preferred?: string | null): string {
  if (preferred) {
    try {
      if (contrastRatio(background, preferred) >= WCAG_AA_RATIO) return preferred
    } catch {
      // preferred が不正な HEX の場合は白黒判定に倒す
    }
  }
  return getContrastTextColor(background)
}
